/**
 * Casca DESKTOP do formulário público de Percepção de Riscos (IMGR): capa →
 * identificação → um bloco por tela (afirmativas com escala em linha) →
 * conclusão. Visual do design aprovado; dados e envio reais do sistema.
 */
import { CARGOS } from "@caint/data";
import type { FormularioPublico } from "@caint/shared";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  ESCALA_TOPO,
  type Enviar,
  Icone,
  Marca,
  SECOES,
  TOTAL,
  cargosPorArea,
  corEscala,
} from "./percepcao.tsx";

type Fase = "cover" | "ident" | "block" | "done";

export function PercepcaoDesktop({
  meta,
  enviar,
  enviando,
}: {
  meta: FormularioPublico;
  enviar: Enviar;
  enviando: boolean;
}) {
  const [fase, setFase] = useState<Fase>("cover");
  const [bi, setBi] = useState(0);
  const [cargoIdx, setCargoIdx] = useState<number | null>(null);
  const [setor, setSetor] = useState("");
  const [ans, setAns] = useState<Record<string, number>>({});
  const [destaque, setDestaque] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  // Cargos da unidade (da matriz), com fallback no catálogo global.
  const cargos = useRef(meta.cargos?.length ? meta.cargos : CARGOS).current;
  const areas = useRef(cargosPorArea(cargos)).current;

  // biome-ignore lint/correctness/useExhaustiveDependencies: bi/fase são gatilhos intencionais — reposiciona o scroll e limpa o destaque ao trocar de bloco/fase.
  useEffect(() => {
    setDestaque(false);
    mainRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [bi, fase]);

  const respondidas = Object.keys(ans).length;
  const pct = Math.round((respondidas / TOTAL) * 100);
  const sec = SECOES[bi];
  const sel = cargoIdx != null ? cargos[cargoIdx] : null;
  const blocoOk = !!sec && sec.perguntas.every((p) => ans[String(p.num)] != null);
  const respBloco = sec ? sec.perguntas.filter((p) => ans[String(p.num)] != null).length : 0;

  function setA(num: number, v: number) {
    setAns((a) => ({ ...a, [String(num)]: v }));
  }

  async function finalizar() {
    if (cargoIdx == null) return;
    const c = cargos[cargoIdx];
    if (!c) return;
    setErro(null);
    const r = await enviar({
      cargo: c.cargo,
      nivel: c.nivel,
      area: c.area,
      perfil: c.perfil,
      setor: setor.trim() || undefined,
      respostas: ans,
    });
    if (r === "ok") {
      setFase("done");
    } else if (typeof r === "object") {
      setErro(r.erro);
    }
    // "fechado" é tratado pela casca orquestradora (troca de tela).
  }

  function proximo() {
    if (!sec) return;
    if (!blocoOk) {
      setDestaque(true);
      const falta = sec.perguntas.find((p) => ans[String(p.num)] == null);
      if (falta)
        document.getElementById(`stmt-${falta.num}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (bi < SECOES.length - 1) {
      setBi(bi + 1);
      return;
    }
    // Último bloco completo: só envia com TODAS as afirmativas respondidas
    // (o stepper permite pular blocos); senão leva ao 1º bloco incompleto.
    if (respondidas < TOTAL) {
      const alvo = SECOES.findIndex((s) => s.perguntas.some((p) => ans[String(p.num)] == null));
      if (alvo >= 0) {
        setDestaque(true);
        setBi(alvo);
      }
      return;
    }
    void finalizar();
  }
  function anterior() {
    if (bi > 0) setBi(bi - 1);
    else setFase("ident");
  }

  // ---------- CAPA ----------
  if (fase === "cover") {
    return (
      <div className="d-wrap">
        <div className="d-main">
          <div className="d-cover">
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
              <Marca size={48} />
            </div>
            <div className="eyebrow">Percepção de Riscos · EMSERH · CAINT</div>
            <h1>Como a equipe enxerga os riscos na unidade</h1>
            <p>
              São {TOTAL} afirmativas curtas, em {SECOES.length} blocos. Para cada uma, diga o quanto concorda
              com o que acontece no dia a dia. Não existe resposta certa — queremos a percepção sincera da
              equipe.
            </p>
            <div className="d-facts">
              <div className="d-fact">
                <span className="fi">
                  <Icone nome="clock" s={20} />
                </span>
                <b>Cerca de 5 minutos</b>
                <span>As respostas ficam salvas se precisar parar.</span>
              </div>
              <div className="d-fact">
                <span className="fi">
                  <Icone nome="lock" s={20} />
                </span>
                <b>Confidencial</b>
                <span>Usada só para entender a cultura de risco da unidade.</span>
              </div>
              <div className="d-fact">
                <span className="fi">
                  <Icone nome="heart" s={20} />
                </span>
                <b>Sem certo ou errado</b>
                <span>Responda pensando no que realmente acontece.</span>
              </div>
            </div>
            <button
              type="button"
              className="d-btn primary"
              style={{ marginTop: 32, padding: "15px 34px", fontSize: 16 }}
              onClick={() => setFase("ident")}
            >
              Começar a pesquisa <Icone nome="arrow" s={18} />
            </button>
            <div style={{ marginTop: 18, fontSize: 13, color: "var(--text-faint)" }}>{meta.unidadeNome}</div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- IDENTIFICAÇÃO ----------
  if (fase === "ident") {
    return (
      <div className="d-wrap">
        <div className="d-main">
          <div className="d-ident">
            <div className="eyebrow" style={{ fontFamily: "var(--font-mono)", color: "var(--primary)" }}>
              Antes de começar
            </div>
            <h1>Identificação</h1>
            <p>
              As respostas são anônimas — usamos apenas o seu cargo e o setor para entender a percepção por
              nível e área.
            </p>
            <div className="d-ident-card">
              <label className="d-lbl" htmlFor="cargo">
                Seu cargo
              </label>
              <select
                id="cargo"
                className="d-select"
                value={cargoIdx ?? ""}
                onChange={(e) => setCargoIdx(e.target.value === "" ? null : Number(e.target.value))}
              >
                <option value="" disabled>
                  Selecione o cargo…
                </option>
                {areas.map(([area, itens]) => (
                  <optgroup key={area} label={area}>
                    {itens.map(({ c, idx }) => (
                      <option key={idx} value={idx}>
                        {c.cargo}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {sel && (
                <div className="d-chips">
                  <span className="d-chip">
                    Nível <b>{sel.nivel}</b>
                  </span>
                  <span className="d-chip">
                    Área <b>{sel.area}</b>
                  </span>
                  <span className="d-chip">
                    Perfil <b>{sel.perfil}</b>
                  </span>
                </div>
              )}
              <label className="d-lbl" htmlFor="setor" style={{ marginTop: 18 }}>
                Setor de lotação (opcional)
              </label>
              <input
                id="setor"
                className="d-input"
                type="text"
                maxLength={80}
                value={setor}
                placeholder="Ex.: Centro Obstétrico"
                onChange={(e) => setSetor(e.target.value)}
              />
            </div>
            <div className="d-nav">
              <button type="button" className="d-btn ghost" onClick={() => setFase("cover")}>
                <Icone nome="back" s={17} /> Voltar
              </button>
              <button
                type="button"
                className="d-btn primary"
                disabled={cargoIdx == null}
                onClick={() => setFase("block")}
              >
                Começar afirmativas <Icone nome="arrow" s={17} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- CONCLUSÃO ----------
  if (fase === "done") {
    return (
      <div className="d-wrap">
        <div className="d-main">
          <div className="d-done">
            <div className="ring">
              <Icone nome="check" s={44} w={2.4} />
            </div>
            <h1>Obrigado pela sua percepção!</h1>
            <p>
              As {respondidas} respostas foram registradas e ajudam a entender a cultura de risco e segurança
              da unidade.
            </p>
            <div className="d-summary">
              <div className="d-sum">
                <div className="v">
                  {respondidas}/{TOTAL}
                </div>
                <div className="l">Afirmativas respondidas</div>
              </div>
              <div className="d-sum">
                <div className="v">{SECOES.length}</div>
                <div className="l">Blocos avaliados</div>
              </div>
              <div className="d-sum">
                <div className="v">{pct}%</div>
                <div className="l">Preenchimento</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- BLOCO ----------
  if (!sec) return null;
  const ultimo = bi === SECOES.length - 1;
  return (
    <div className="d-wrap">
      <div className="d-top">
        <div className="d-top-inner">
          <div className="d-brand">
            <Marca size={30} />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
              <b>CAINT</b>
              <span>Percepção de Riscos</span>
            </div>
          </div>
          <div className="d-top-prog">
            <div className="d-bar">
              <i style={{ width: `${pct}%` }} />
            </div>
            <span className="d-pct">{pct}%</span>
          </div>
        </div>
      </div>

      <div className="d-main" ref={mainRef}>
        <div className="d-steps">
          {SECOES.map((s, i) => (
            <Fragment key={s.id}>
              {i > 0 && <span className="d-step-line" />}
              <button
                type="button"
                className={`d-step ${i < bi ? "done" : i === bi ? "active" : "todo"}`}
                style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
                onClick={() => setBi(i)}
              >
                <span className="dot">{i < bi ? "✓" : i + 1}</span>
                <span className="nm">{s.nome}</span>
              </button>
            </Fragment>
          ))}
        </div>

        <div className="d-sec-head">
          <div className="d-sec-icon" style={{ background: sec.cor }}>
            <Icone nome={sec.icone} s={28} />
          </div>
          <div>
            <h2>{sec.nome}</h2>
            <div className="sub">
              Bloco {bi + 1} de {SECOES.length} · {sec.perguntas.length} afirmativas
            </div>
          </div>
        </div>
        <p className="d-sec-lead">
          {sec.intro} <b>Marque o quanto concorda com cada afirmativa.</b>
        </p>

        {sec.perguntas.map((p) => {
          const val = ans[String(p.num)];
          const pend = destaque && val == null;
          return (
            <div
              key={p.num}
              id={`stmt-${p.num}`}
              className={`d-stmt${val != null ? " answered" : ""}${pend ? " pending-hl" : ""}`}
            >
              <div className="d-stmt-q">
                <span className="qn">{p.num}</span>
                <span className="qt">{p.texto}</span>
              </div>
              <div className="d-scale">
                {ESCALA_TOPO.map((o) => (
                  <button
                    type="button"
                    key={o.valor}
                    className={`d-scale-opt${val === o.valor ? " sel" : ""}`}
                    style={{ ["--clr" as string]: corEscala(o.valor) }}
                    onClick={() => setA(p.num, o.valor)}
                  >
                    <span className="sdot" />
                    <span className="st">{o.rotulo}</span>
                    <span className="sd">{o.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {erro && (
          <p style={{ textAlign: "right", marginTop: 12, color: "var(--danger)", fontWeight: 600 }}>{erro}</p>
        )}

        <div className="d-nav">
          <button type="button" className="d-btn ghost" onClick={anterior} disabled={enviando}>
            <Icone nome="back" s={17} /> Voltar
          </button>
          <span className="count">
            <b>{respBloco}</b> de {sec.perguntas.length} respondidas neste bloco
          </span>
          <button type="button" className="d-btn primary" onClick={proximo} disabled={enviando}>
            {enviando ? "Enviando…" : ultimo ? "Concluir" : "Próximo bloco"} <Icone nome="arrow" s={17} />
          </button>
        </div>
        {destaque && !blocoOk && (
          <div
            style={{
              textAlign: "right",
              marginTop: 10,
              fontSize: 13,
              color: "var(--warning)",
              fontWeight: 600,
            }}
          >
            Responda todas as afirmativas deste bloco para avançar.
          </div>
        )}
      </div>
    </div>
  );
}
